// FR-M9: Settings' Calendars tab — where a calendar connection is managed once it exists: each
// provider's state, the connected account, the notes permission, Connect and Disconnect. One
// section per org, since a connection belongs to a user within an org. The Meetings page keeps
// the first Connect (it is empty without a calendar) and a "Manage calendars" link here.
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Callout } from "@grackle/ui";
import { ApiFail } from "../api/client";
import type { OrgOut } from "../api/types";
import CalendarProviderBlock, { providerVisible } from "../library/CalendarProviderBlock";
import { useIsPlatformAdmin } from "../ops/platformAdmin";
import { calendarStatus, disconnectCalendar, stubConnect } from "./client";
import { calendarConnectNotice, type CalendarProvider } from "./connectNotice";
import type { CalendarStatus } from "./types";

const PROVIDERS: { provider: CalendarProvider; label: string }[] = [
  { provider: "google", label: "Google Calendar" },
  { provider: "microsoft", label: "Microsoft 365" },
];

/** `?connected=` / `?calendar_error=` from a connect started here (the server returns to
 * `/account?tab=calendars`), shown above every org's section. */
export function CalendarConnectNotice() {
  const [params] = useSearchParams();
  const notice = calendarConnectNotice(params);
  if (!notice) return null;
  return (
    <Callout
      tone={notice.tone}
      icon={notice.tone === "success" ? "check-circle" : "warning-circle"}
      role={notice.tone === "success" ? "status" : "alert"}
    >
      {notice.message}
    </Callout>
  );
}

export function CalendarSettingsSection({ org, showOrgName }: { org: OrgOut; showOrgName: boolean }) {
  const [, setParams] = useSearchParams();
  const platformAdmin = useIsPlatformAdmin();
  const [status, setStatus] = useState<CalendarStatus | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setStatus(await calendarStatus(org.id));
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Could not load your calendars.");
    }
  }, [org.id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function onStub(provider: CalendarProvider) {
    setBusy(`stub-${provider}`);
    setError(null);
    try {
      await stubConnect(org.id, provider);
      await load();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Stub connect failed.");
    } finally {
      setBusy(null);
    }
  }

  async function onDisconnect(connectionId: string) {
    setBusy(`disc-${connectionId}`);
    setError(null);
    try {
      await disconnectCalendar(org.id, connectionId);
      // A "Connected …" notice from the OAuth redirect would now be stale.
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const key of ["connected", "calendar_error", "provider"]) next.delete(key);
          return next;
        },
        { replace: true },
      );
      await load();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Could not disconnect.");
    } finally {
      setBusy(null);
    }
  }

  const stub = status?.stub ?? false;
  const visible = PROVIDERS.filter((p) => providerVisible(status?.[p.provider], stub, platformAdmin));

  return (
    <div>
      <div className="account-section-title">{showOrgName ? `Calendars · ${org.name}` : "Calendars"}</div>
      <div className="account-section-sub">
        Read-only access to upcoming meetings, so the Meetings page can list them and the notetaker can join.
      </div>
      {error ? (
        <Callout tone="danger" icon="warning-circle">
          {error}
        </Callout>
      ) : null}
      {status && visible.length === 0 ? (
        <p className="gk-sm">Calendar connections aren&apos;t available on this server yet.</p>
      ) : (
        visible.map((p) => (
          <CalendarProviderBlock
            key={p.provider}
            label={p.label}
            provider={p.provider}
            orgId={org.id}
            info={status?.[p.provider]}
            stub={stub}
            busy={busy}
            onStub={() => void onStub(p.provider)}
            onDisconnect={(id) => void onDisconnect(id)}
            platformAdmin={platformAdmin}
            returnTo="settings"
          />
        ))
      )}
    </div>
  );
}
